import type {
  BrandId,
  CommissionRateRecord,
  ProcurementRiseAlertEntry,
  SalesCostSkuEntry,
} from '@nadoutong/contracts';
import { BRAND_IDS, DEFAULT_BRAND_ID, normalizeBrandId } from '@nadoutong/contracts';
import { useMemo, useState } from 'react';
import {
  useBulkUpsertSalesCosts,
  useSalesCosts,
  useUpsertSalesCost,
} from '../api/salesCosts.ts';
import { useCommissionRates, useCreateCommissionRate } from '../api/commissionRates.ts';
import { useSkuSpread } from '../api/commissionReports.ts';
import { OpsApiError } from '../api/client.ts';
import { ROLE_OPS_ADMIN, hasAnyOpsRole } from '../auth/opsRoles.ts';
import { OpsUserRef } from '../components/identity/OpsUserRef.tsx';
import { useToast } from '../components/Toast.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import {
  Button,
  Chip,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Panel,
  Select,
  TBody,
  Table,
  TableEmpty,
  TableSkeleton,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

/**
 * /crm/pricing — lead pricing panel (epic #1032 E3-P1, #1102).
 *
 * Per-SKU × brand sales costs, grouped by namespace. Leads see the
 * procurement cost here BY DESIGN — the spread between procurement and
 * sales cost is their business to manage (the regular-sales projection
 * that hides procurement is P2 #1104). Role gate: admin + sales lead
 * only; the ops-api layer additionally pins lead sessions to their
 * sales-profile brand.
 *
 * Bulk editing drives the ATOMIC `/v1/ops/sales-costs/bulk` endpoint:
 * one request per namespace group, all rows or none — deliberately NOT
 * the per-SKU fan-out the customer-360 rate editor still uses.
 */

function errorText(error: Error): string {
  return error instanceof OpsApiError ? `${error.code}: ${error.message}` : error.message;
}

function formatMultiplier(value: number | null): string {
  if (value === null) return '—';
  if (!Number.isFinite(value)) return '∞';
  return value.toFixed(4);
}

function formatCost(value: number | null): string {
  return value === null ? '—' : value.toFixed(2);
}

function parseDraft(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

const PERIOD_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Percent string → integer basis points, or null when out of range /
 * not representable as a whole bps (e.g. 5.255% → 525.5 bps). */
function parsePercentToBps(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const percent = Number(trimmed);
  if (!Number.isFinite(percent) || percent < 0 || percent > 100) return null;
  const bps = Math.round(percent * 100);
  if (Math.abs(percent * 100 - bps) > 1e-6) return null;
  return bps >= 0 && bps <= 10_000 ? bps : null;
}

/** Basis points → a display percentage, e.g. 525 → "5.25%". */
function formatBpsPercent(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`;
}

interface NamespaceGroup {
  readonly namespaceSlug: string;
  readonly vendor: string;
  readonly skus: readonly SalesCostSkuEntry[];
}

function groupByNamespace(skus: readonly SalesCostSkuEntry[]): NamespaceGroup[] {
  const groups = new Map<string, SalesCostSkuEntry[]>();
  for (const sku of skus) {
    const list = groups.get(sku.namespace_slug);
    if (list === undefined) {
      groups.set(sku.namespace_slug, [sku]);
    } else {
      list.push(sku);
    }
  }
  return [...groups.entries()].map(([namespaceSlug, list]) => ({
    namespaceSlug,
    vendor: list[0]?.vendor ?? '',
    skus: list,
  }));
}

export function LeadPricingPage(): JSX.Element {
  const t = useT();
  const { user, status } = useSession();
  const isAdmin = hasAnyOpsRole(user?.roles ?? [], [ROLE_OPS_ADMIN]);
  const [adminBrand, setAdminBrand] = useState<BrandId>(DEFAULT_BRAND_ID);
  // Lead sessions send no brand — ops-api pins their profile brand.
  const brand = isAdmin ? adminBrand : null;
  // Wait for the session before fetching: admin requests must carry an
  // explicit brand, and the roles are unknown until the session loads.
  const query = useSalesCosts(brand, status === 'authenticated');

  const groups = useMemo(() => groupByNamespace(query.data?.skus ?? []), [query.data]);
  const policyRate = query.data?.policy_rate ?? null;
  const alerts = query.data?.alerts ?? [];

  return (
    <section className="flex flex-col gap-8" data-testid="lead-pricing-page">
      <PageHeader
        title={t('lead_pricing.title')}
        sub={t('lead_pricing.sub')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {isAdmin ? (
              <Select
                value={adminBrand}
                onValueChange={(next) => {
                  const parsed = normalizeBrandId(next);
                  if (parsed !== null) setAdminBrand(parsed);
                }}
                options={BRAND_IDS.map((id) => ({ value: id, label: id }))}
                ariaLabel={t('lead_pricing.brand_aria')}
                mono
                testId="lead-pricing-brand-select"
              />
            ) : (
              <Chip tone="signal" testId="lead-pricing-brand-chip">
                {query.data?.brand ?? '…'}
              </Chip>
            )}
            {policyRate !== null ? (
              <Chip tone="neutral" lamp={false} testId="lead-pricing-policy-rate">
                {t('lead_pricing.policy_rate', { rate: policyRate.toFixed(4) })}
              </Chip>
            ) : null}
          </div>
        }
      />

      {alerts.length > 0 ? <ProcurementRiseBanner alerts={alerts} /> : null}

      <CommissionRatePanel brand={brand} enabled={status === 'authenticated'} />

      {/* Procurement-vs-sales-cost spread (epic #1034 E5-M5, #1147): stays
          on the lead pricing page because it exposes procurement — admin +
          sales-lead only, never finance. The commission leaderboard lives
          on its own finance-reachable page (/crm/leaderboard). */}
      <SkuSpreadPanel brand={brand} enabled={status === 'authenticated'} />

      {query.isLoading ? <TableSkeleton rows={6} /> : null}

      {query.isError ? (
        <EmptyState
          title={t('lead_pricing.error.load')}
          hint={errorText(query.error instanceof Error ? query.error : new Error(String(query.error)))}
          testId="lead-pricing-error"
        />
      ) : null}

      {query.isSuccess && groups.length === 0 ? (
        <EmptyState
          title={t('lead_pricing.empty.title')}
          hint={t('lead_pricing.empty.hint')}
          testId="lead-pricing-empty"
        />
      ) : null}

      {groups.map((group) => (
        <NamespaceSection key={group.namespaceSlug} group={group} brand={brand} policyRate={policyRate} />
      ))}
    </section>
  );
}

/**
 * Brand-scoped procurement-rise risk feed (epic #1032 E3-P4, #1107): the
 * SKUs whose effective procurement cost has risen above this brand's
 * sales cost. Surfaced here — on the lead pricing panel — so the AC's
 * "lead-visible risk item" does not depend on the admin-only unified
 * risk feed. Leads see procurement values by design.
 */
function ProcurementRiseBanner({
  alerts,
}: {
  readonly alerts: readonly ProcurementRiseAlertEntry[];
}): JSX.Element {
  const t = useT();
  return (
    <Panel
      caption={
        <span className="flex items-baseline gap-2">
          <span className="text-bad">{t('lead_pricing.alerts.title')}</span>
          <span className="text-dense font-normal text-paper-low">{t('lead_pricing.alerts.sub')}</span>
        </span>
      }
      count={alerts.length}
      testId="lead-pricing-alerts"
    >
      <Table>
        <THead>
          <Tr>
            <Th>{t('lead_pricing.alerts.table.model')}</Th>
            <Th num>{t('lead_pricing.alerts.table.procurement')}</Th>
            <Th num>{t('lead_pricing.alerts.table.sales_cost')}</Th>
            <Th>{t('lead_pricing.alerts.table.since')}</Th>
          </Tr>
        </THead>
        <TBody>
          {alerts.map((alert) => (
            <Tr key={alert.fingerprint} data-testid={`lead-pricing-alert-${alert.sku_id}`}>
              <Td mono emphasis>
                {alert.sku_name}
              </Td>
              <Td num>
                <span className="text-bad">{formatCost(alert.procurement_cny_per_usd)}</span>
              </Td>
              <Td num>{formatCost(alert.sales_cost_cny_per_usd)}</Td>
              <Td>{alert.first_alerted_at.slice(0, 10)}</Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </Panel>
  );
}

/**
 * Per-brand commission rate management (epic #1034 E5-M2, #1144). Lives
 * on the lead pricing panel — leads already manage the brand's sales
 * economics here and the role gate is identical (admin + sales lead). The
 * rate is APPEND-ONLY + effective-dated: history is immutable, and a new
 * rate may only take effect from the current period forward (the platform
 * rejects retroactive effective dates). Rates are entered as a percentage
 * and stored as basis points.
 */
function CommissionRatePanel({
  brand,
  enabled,
}: {
  readonly brand: BrandId | null;
  readonly enabled: boolean;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const query = useCommissionRates(brand, enabled);
  const createMut = useCreateCommissionRate();
  const [percentDraft, setPercentDraft] = useState('');
  const [effectiveDraft, setEffectiveDraft] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const currentPeriod = query.data?.current_period ?? null;
  const currentRate = query.data?.current_rate ?? null;
  const rates = query.data?.rates ?? [];
  const previewBps = parsePercentToBps(percentDraft);

  async function handleAppend(): Promise<void> {
    setFormError(null);
    const bps = parsePercentToBps(percentDraft);
    if (bps === null) {
      setFormError(t('commission_rate.error.percent'));
      return;
    }
    if (!PERIOD_RE.test(effectiveDraft)) {
      setFormError(t('commission_rate.error.period'));
      return;
    }
    if (currentPeriod !== null && effectiveDraft < currentPeriod) {
      setFormError(t('commission_rate.error.retroactive', { period: currentPeriod }));
      return;
    }
    try {
      await createMut.mutateAsync({
        brand,
        body: { rate_bps: bps, effective_from: effectiveDraft },
      });
      setPercentDraft('');
      setEffectiveDraft('');
      toast.success(t('commission_rate.toast.appended'));
    } catch (err) {
      setFormError(errorText(err instanceof Error ? err : new Error(String(err))));
    }
  }

  return (
    <Panel
      caption={
        <span className="flex items-baseline gap-2">
          <span>{t('commission_rate.title')}</span>
          <span className="text-dense font-normal text-paper-low">{t('commission_rate.sub')}</span>
        </span>
      }
      count={rates.length}
      testId="commission-rate-panel"
    >
      {query.data === undefined ? (
        // Gate on data presence, not `isLoading`: the query is disabled
        // until the session authenticates (enabled = authenticated), and a
        // disabled query reports `isLoading: false` in React Query v5 — so
        // gating on isLoading would flash the success shell with no data,
        // then blank to loading once the fetch starts. Data-gating keeps a
        // single stable loading→success transition.
        query.isError ? (
          <p className="m-0 px-4 py-3 text-dense text-bad" data-testid="commission-rate-error">
            {errorText(query.error instanceof Error ? query.error : new Error(String(query.error)))}
          </p>
        ) : (
          <p className="m-0 px-4 py-3 text-dense text-paper-low" data-testid="commission-rate-loading">
            {t('common.loading')}
          </p>
        )
      ) : (
        <div className="flex flex-col gap-4 p-4">
          <p className="m-0 text-dense text-paper-low" data-testid="commission-rate-current">
            {currentPeriod === null
              ? t('commission_rate.current.unknown')
              : currentRate === null
                ? t('commission_rate.current.none', { period: currentPeriod })
                : t('commission_rate.current.value', {
                    period: currentPeriod,
                    rate: formatBpsPercent(currentRate.rate_bps),
                    effective: currentRate.effective_from,
                  })}
          </p>

          <div className="flex flex-wrap items-end gap-3">
            <FormField
              label={t('commission_rate.form.percent_label')}
              fieldId="commission-rate-percent"
              hint={
                previewBps === null
                  ? t('commission_rate.form.percent_hint')
                  : t('commission_rate.form.percent_preview', { bps: previewBps })
              }
            >
              <Input
                id="commission-rate-percent"
                mono
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={percentDraft}
                onChange={(e) => setPercentDraft(e.currentTarget.value)}
                className="w-32"
                data-testid="commission-rate-percent-input"
              />
            </FormField>
            <FormField
              label={t('commission_rate.form.effective_label')}
              fieldId="commission-rate-effective"
              hint={
                currentPeriod === null
                  ? t('commission_rate.form.effective_hint')
                  : t('commission_rate.form.effective_min', { period: currentPeriod })
              }
            >
              <Input
                id="commission-rate-effective"
                mono
                type="month"
                value={effectiveDraft}
                onChange={(e) => setEffectiveDraft(e.currentTarget.value)}
                className="w-40"
                data-testid="commission-rate-effective-input"
              />
            </FormField>
            <Button
              // Validation lives in handleAppend so the operator gets a
              // specific reason (bad percent / bad month / retroactive)
              // instead of a silently disabled button. The picker's `min`
              // is deliberately omitted — the JS check + the server gate
              // are the real enforcement, and a hard `min` would let a
              // browser silently clamp a typed past month.
              disabled={createMut.isPending}
              onClick={() => void handleAppend()}
              data-testid="commission-rate-append"
            >
              {t('commission_rate.form.append')}
            </Button>
          </div>

          {formError !== null ? (
            <p
              role="alert"
              className="m-0 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
              data-testid="commission-rate-form-error"
            >
              {formError}
            </p>
          ) : null}

          {rates.length === 0 ? (
            <EmptyState
              title={t('commission_rate.empty.title')}
              hint={t('commission_rate.empty.hint')}
              testId="commission-rate-empty"
            />
          ) : (
            <Table>
              <THead>
                <Tr>
                  <Th>{t('commission_rate.table.effective_from')}</Th>
                  <Th num>{t('commission_rate.table.rate')}</Th>
                  <Th>{t('commission_rate.table.created_by')}</Th>
                  <Th>{t('commission_rate.table.created_at')}</Th>
                </Tr>
              </THead>
              <TBody>
                {rates.map((rate) => (
                  <CommissionRateRow
                    key={rate.id}
                    rate={rate}
                    isCurrent={currentRate !== null && currentRate.id === rate.id}
                  />
                ))}
              </TBody>
            </Table>
          )}
        </div>
      )}
    </Panel>
  );
}

function CommissionRateRow({
  rate,
  isCurrent,
}: {
  readonly rate: CommissionRateRecord;
  readonly isCurrent: boolean;
}): JSX.Element {
  const t = useT();
  return (
    <Tr data-testid={`commission-rate-row-${rate.effective_from}`}>
      <Td>
        <span className="flex items-center gap-2">
          <span className="font-mono">{rate.effective_from}</span>
          {isCurrent ? (
            <Chip tone="signal" lamp={false} testId={`commission-rate-current-badge-${rate.effective_from}`}>
              {t('commission_rate.table.current_badge')}
            </Chip>
          ) : null}
        </span>
      </Td>
      <Td num mono emphasis>
        {formatBpsPercent(rate.rate_bps)}
      </Td>
      <Td mono>
        <OpsUserRef id={rate.created_by_ops_user_id} />
      </Td>
      <Td>{rate.created_at.slice(0, 10)}</Td>
    </Tr>
  );
}

interface NamespaceSectionProps {
  readonly group: NamespaceGroup;
  readonly brand: BrandId | null;
  readonly policyRate: number | null;
}

function NamespaceSection({ group, brand, policyRate }: NamespaceSectionProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const bulkMut = useBulkUpsertSalesCosts();
  const [bulkDraft, setBulkDraft] = useState('');
  const [bulkError, setBulkError] = useState<string | null>(null);

  const bulkValue = parseDraft(bulkDraft);

  async function handleBulkApply(): Promise<void> {
    if (bulkValue === null) return;
    setBulkError(null);
    try {
      // ONE atomic request for the whole namespace group — the platform
      // applies every row in a single transaction or none of them.
      const result = await bulkMut.mutateAsync({
        brand,
        body: {
          items: group.skus.map((sku) => ({
            sku_id: sku.sku_id,
            sales_cost_cny_per_usd: bulkValue,
            note: sku.note,
          })),
        },
      });
      setBulkDraft('');
      toast.success(t('lead_pricing.toast.bulk_applied', { count: result.applied }));
    } catch (err) {
      setBulkError(errorText(err instanceof Error ? err : new Error(String(err))));
    }
  }

  return (
    <Panel
      caption={
        <span className="flex items-baseline gap-2">
          <span className="font-mono">{group.namespaceSlug}</span>
          <span className="text-dense font-normal text-paper-low">{group.vendor}</span>
        </span>
      }
      count={group.skus.length}
      actions={
        <div className="flex items-center gap-1.5">
          <Input
            mono
            type="number"
            step="0.01"
            value={bulkDraft}
            onChange={(e) => setBulkDraft(e.currentTarget.value)}
            aria-label={t('lead_pricing.bulk.label')}
            placeholder={t('lead_pricing.bulk.label')}
            className="w-32"
            data-testid={`lead-pricing-bulk-input-${group.namespaceSlug}`}
          />
          <Button
            size="sm"
            disabled={bulkMut.isPending || bulkValue === null}
            onClick={() => void handleBulkApply()}
            data-testid={`lead-pricing-bulk-apply-${group.namespaceSlug}`}
          >
            {t('lead_pricing.bulk.apply')}
          </Button>
        </div>
      }
      testId={`lead-pricing-group-${group.namespaceSlug}`}
    >
      {bulkError !== null ? (
        <p
          className="m-0 border-b border-line-soft px-4 py-2 text-dense text-bad"
          data-testid={`lead-pricing-bulk-error-${group.namespaceSlug}`}
        >
          {bulkError}
        </p>
      ) : null}
      <Table>
        <THead>
          <Tr>
            <Th>{t('lead_pricing.table.model')}</Th>
            <Th num>{t('lead_pricing.table.procurement')}</Th>
            <Th num>{t('lead_pricing.table.procurement_floor')}</Th>
            <Th num>{t('lead_pricing.table.sales_cost')}</Th>
            <Th num>{t('lead_pricing.table.sales_floor')}</Th>
            <Th>{t('lead_pricing.table.note')}</Th>
            <Th num>{t('lead_pricing.table.actions')}</Th>
          </Tr>
        </THead>
        <TBody>
          {group.skus.map((sku) => (
            <SkuRow key={sku.sku_id} sku={sku} brand={brand} policyRate={policyRate} />
          ))}
        </TBody>
      </Table>
    </Panel>
  );
}

interface SkuRowProps {
  readonly sku: SalesCostSkuEntry;
  readonly brand: BrandId | null;
  readonly policyRate: number | null;
}

function SkuRow({ sku, brand, policyRate }: SkuRowProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const upsertMut = useUpsertSalesCost();
  const [draft, setDraft] = useState('');
  const [noteDraft, setNoteDraft] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const draftValue = parseDraft(draft);
  // Note-only edits are savable on rows that already carry a sales
  // cost: Save falls back to the stored value when no new cost is
  // typed. Rows without a cost still need one before anything saves.
  const saveValue = draftValue ?? sku.sales_cost_cny_per_usd;
  const noteChanged = noteDraft !== null && noteDraft !== (sku.note ?? '');
  const canSave = draftValue !== null || (noteChanged && saveValue !== null);
  const previewFloor =
    draftValue !== null && policyRate !== null && policyRate > 0
      ? draftValue / policyRate
      : sku.sales_floor_multiplier;

  async function save(nextValue: number | null): Promise<void> {
    setRowError(null);
    try {
      await upsertMut.mutateAsync({
        skuId: sku.sku_id,
        brand,
        body: {
          sales_cost_cny_per_usd: nextValue,
          note: noteDraft ?? sku.note,
        },
      });
      setDraft('');
      setNoteDraft(null);
      toast.success(
        nextValue === null ? t('lead_pricing.toast.cleared') : t('lead_pricing.toast.saved'),
      );
    } catch (err) {
      setRowError(errorText(err instanceof Error ? err : new Error(String(err))));
    }
  }

  return (
    <>
      <Tr data-testid={`lead-pricing-row-${sku.sku_id}`}>
        <Td mono emphasis>
          {sku.model}
        </Td>
        <Td num>{formatCost(sku.upstream_cost_cny_per_usd)}</Td>
        <Td num>{formatMultiplier(sku.minimum_rate_multiplier)}</Td>
        <Td>
          <Input
            mono
            type="number"
            step="0.01"
            value={draft}
            placeholder={
              sku.sales_cost_cny_per_usd === null
                ? t('lead_pricing.row.not_set')
                : sku.sales_cost_cny_per_usd.toFixed(2)
            }
            onChange={(e) => setDraft(e.currentTarget.value)}
            aria-label={t('lead_pricing.table.sales_cost')}
            className="ml-auto w-28"
            data-testid={`lead-pricing-cost-input-${sku.sku_id}`}
          />
        </Td>
        <Td num data-testid={`lead-pricing-floor-${sku.sku_id}`}>
          {formatMultiplier(previewFloor)}
        </Td>
        <Td>
          <Input
            value={noteDraft ?? sku.note ?? ''}
            onChange={(e) => setNoteDraft(e.currentTarget.value)}
            aria-label={t('lead_pricing.table.note')}
            className="w-36"
            data-testid={`lead-pricing-note-input-${sku.sku_id}`}
          />
        </Td>
        <Td>
          <div className="flex items-center justify-end gap-1.5">
            <Button
              size="sm"
              disabled={upsertMut.isPending || !canSave}
              onClick={() => void save(saveValue)}
              data-testid={`lead-pricing-save-${sku.sku_id}`}
            >
              {t('lead_pricing.row.save')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={upsertMut.isPending || sku.sales_cost_cny_per_usd === null}
              onClick={() => void save(null)}
              data-testid={`lead-pricing-clear-${sku.sku_id}`}
            >
              {t('lead_pricing.row.clear')}
            </Button>
          </div>
        </Td>
      </Tr>
      {rowError !== null ? (
        <Tr>
          <Td colSpan={7}>
            <span className="text-dense text-bad" data-testid={`lead-pricing-row-error-${sku.sku_id}`}>
              {rowError}
            </span>
          </Td>
        </Tr>
      ) : null}
    </>
  );
}

/**
 * Procurement-vs-sales-cost spread per SKU (epic #1034 E5-M5, #1147). Leads
 * see procurement cost here BY DESIGN (E3 three-tier — the spread is the
 * lead's business to manage); this panel is never served to a CM.
 */
function SkuSpreadPanel({
  brand,
  enabled,
}: {
  readonly brand: BrandId | null;
  readonly enabled: boolean;
}): JSX.Element {
  const t = useT();
  const query = useSkuSpread(brand, enabled);
  const rows = query.data?.skus ?? [];
  return (
    <Panel
      caption={
        <span className="flex items-baseline gap-2">
          <span>{t('sku_spread.title')}</span>
          <span className="text-dense font-normal text-paper-low">{t('sku_spread.sub')}</span>
        </span>
      }
      count={rows.length}
      testId="sku-spread"
    >
      {query.isLoading ? <TableSkeleton rows={4} /> : null}
      {query.isError ? (
        <EmptyState
          title={t('sku_spread.error')}
          hint={errorText(query.error instanceof Error ? query.error : new Error(String(query.error)))}
          testId="sku-spread-error"
        />
      ) : null}
      {query.isSuccess ? (
        <Table>
          <THead>
            <Tr>
              <Th>{t('sku_spread.table.model')}</Th>
              <Th num>{t('sku_spread.table.procurement')}</Th>
              <Th num>{t('sku_spread.table.sales_cost')}</Th>
              <Th num>{t('sku_spread.table.spread')}</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.length === 0 ? (
              <TableEmpty colSpan={4} testId="sku-spread-empty">
                {t('sku_spread.empty')}
              </TableEmpty>
            ) : (
              rows.map((row) => (
                <Tr key={row.sku_id} data-testid={`sku-spread-${row.sku_id}`}>
                  <Td mono emphasis>
                    {row.sku_name}
                  </Td>
                  <Td num>{formatCost(row.upstream_cost_cny_per_usd)}</Td>
                  <Td num>{formatCost(row.sales_cost_cny_per_usd)}</Td>
                  <Td num>
                    <span className={row.spread_cny_per_usd !== null && row.spread_cny_per_usd < 0 ? 'text-bad' : undefined}>
                      {formatCost(row.spread_cny_per_usd)}
                    </span>
                  </Td>
                </Tr>
              ))
            )}
          </TBody>
        </Table>
      ) : null}
    </Panel>
  );
}
